<template>
  <div id="app" class="bg-[#fff] max-w-[800px] mx-auto relative min-h-[100vh] font-montserrat">
    <div
      v-if="pullDistance > 0 || isRefreshing"
      class="fixed -top-12 left-0 right-0 z-50 flex justify-center pointer-events-none"
    >
      <div
        class="mt-2 px-4 py-2 bg-white rounded-full shadow flex items-center gap-2 text-sm pull-indicator font-bold text-primary"
        :class="{ dragging: pulling }"
        :style="{ transform: `translateY(${pullDistance / 2}px)` }"
      >
        <span
          v-if="!isRefreshing"
          class="transition-transform"
          :style="{
            transform: isRefreshing
              ? 'rotate(360deg)'
              : pullDistance > THRESHOLD
                ? 'rotate(180deg)'
                : 'rotate(0deg)'
          }"
        >
          ↓
        </span>

        <span>{{ pullText }}</span>
      </div>
    </div>

    <router-view v-if="isReady"/>
    <div v-else class="fixed inset-0 bg-primary items-center flex justify-center z-50 text-center px-4">
      <div v-if="!errorLoader" class="flex flex-col gap-3">
        <div class="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
        <div class="text-lg font-semibold text-white">Mohon tunggu...</div>
      </div>
      <div v-else>
        <div class="text-white text-lg font-bold">Gagal memuat data</div>
        <button
          @click="retryFetch"
          class="mt-4 bg-white text-primary px-4 py-2 rounded-lg font-semibold hover:bg-red-100 transition"
        >
          Coba Lagi
        </button>
      </div>
    </div>

    <NavBar v-if="isNavBarVisible" />

    <div   
      class="fixed bg-white z-10 w-10/12 rounded-md
        left-1/2 -translate-x-1/2
        p-3 shadow-2xl
        border-[0.5px] border-neutral-400
        transition-transform duration-400 ease-in
        -translate-y-28 top-0"
      :class="notification ? 'animate-toast-in' : 'animate-toast-out'"
    >

      <div class="font-bold text-primary">{{notificationTitle}}</div>
      <div class="text-sm">{{notificationDescription}}</div>
      
      <div class="absolute bottom-0 bg-gray-300 rounded-md w-full left-0">
        <div class="h-1  overflow-hidden ">
          <div
            class="h-full bg-primary origin-left shadow-md"
            :class="notification ? 'animate-toast-progress' : ''"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { App } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
// import { Capacitor } from '@capacitor/core';
import NavBar from './components/Nav.vue';
import Pusher from 'pusher-js';
import { useAcademicYearStore } from './stores/useAcademicYearStore';
import { useChildrenStore } from './stores/useChildrenStore';
import { useProfileStore } from './stores/useProfileStore';
import { RoleEnum } from './enums/RoleEnum';
import { useNotificationStore } from './stores/useNotificationStore';
import { useConfigStore } from './stores/useConfigStore';
import { PushNotifications } from '@capacitor/push-notifications';
import api from './services/api';
import { refreshBus } from './utils/refreshBus';
import { Capacitor } from '@capacitor/core';

const route = useRoute();
const router = useRouter();

const academicStore = useAcademicYearStore();
const childrenStore = useChildrenStore();
const profileStore = useProfileStore();
const notificationStore = useNotificationStore();
const configStore = useConfigStore();

const isReady = ref(false);
const errorLoader = ref(false);
const isNavBarVisible = computed(() => ['Home', 'Publication', 'Academic', 'Notification'].includes(route.name));
const isBootstrapping = ref(false);

const isParent = computed(() => {
  return profileStore.profile?.roles?.some(role => role.name === RoleEnum.PARENTS) || false;
});

const isNative = Capacitor.isNativePlatform()

const onPullRefresh = async () => {
  refreshBus.emit('refresh')
}

const pullDistance = ref(0)
const pullText = ref('Tarik untuk refresh')
const isRefreshing = ref(false)

let startY = 0
let pulling = false

const THRESHOLD = 100

const onTouchStart = (e) => {
  if (!isNative) return
  if (window.scrollY !== 0) return

  startY = e.touches[0].clientY

  pulling = true
}

const onTouchMove = (e) => {
  if (!pulling || isRefreshing.value) return
  
  const diff = e.touches[0].clientY - startY
  if (diff <= 0) return

  document.documentElement.classList.add('pulling')

  pullDistance.value = Math.min(diff, THRESHOLD + 20)

  pullText.value =
    pullDistance.value > THRESHOLD
      ? 'Lepas untuk refresh'
      : 'Tarik untuk refresh'
}

const onTouchEnd = async () => {
  if (!pulling) return
  pulling = false

  if(!pulling) {
    document.documentElement.classList.remove('pulling')
  }

  if (pullDistance.value > THRESHOLD) {
    isRefreshing.value = true
    pullText.value = 'Memuat...'

    onPullRefresh()

    // simulasi nunggu halaman fetch
    isRefreshing.value = false
    pullDistance.value = 0
    pullText.value = 'Tarik untuk refresh'
  } else {
    pullDistance.value = 0
  }
}


const checkExcludedRoute = async () => {
  if (isBootstrapping.value) return; // ⛔ cegah double call
  isBootstrapping.value = true;
  
  try {
    const excludedRoutes = ['Login', 'Register', 'SplashScreen', 'CloseSelf'];
    if (excludedRoutes.includes(route.name)) {
      isReady.value = true;
      if (pusher.value) {
        if (chatChannel.value) {
          chatChannel.value.unbind_all();
          chatChannel.value.unsubscribe();
          chatChannel.value = null;
        }
        unsubscribeGroupRooms();
        pusher.value.disconnect();
        pusher.value = null;
        window.globalPusher = null;
      }
      return;
    }

    await academicStore.fetchAcademicYear();

    await profileStore.fetchProfile();
    
    await configStore.fetchConfig();
    
    await fetchData();
    isReady.value = true;
  } catch (err) {
    console.error('Gagal fetch awal:', err);
    errorLoader.value = true;
  }
};

const pusher = ref(null);
const chatChannel = ref(null);
const groupRoomChannels = ref([]); // Track subscribed group room channels

const initializePusher = () => {
  if (pusher.value) return;

  const key = process.env.VUE_APP_PUSHER_KEY;
  const cluster = process.env.VUE_APP_PUSHER_CLUSTER;
  const apiURL = process.env.VUE_APP_API_URL;

  if (!key || !cluster || !apiURL) {
    console.warn('Pusher credentials missing in App.vue');
    return;
  }

  pusher.value = new Pusher(key, {
    cluster: cluster,
    forceTLS: true,
    authEndpoint: apiURL + '/broadcasting/auth',
    auth: {
      headers: {
        'Authorization': localStorage.getItem('token')
      }
    }
  });

  window.globalPusher = pusher.value;

  const channelName = 'private-user-chat.' + profileStore.profile.id;
  chatChannel.value = pusher.value.subscribe(channelName);

  chatChannel.value.bind('message.sent', (data) => {
    const isChatPage = route.name === 'QAChat' || route.name === 'GroupChatRoom';
    if (!isChatPage) {
      showNotification(data.sender_name, data.message);
    }
  });
};

// Helper: produce a readable preview for activity/photo messages
const buildGroupMessagePreview = (message) => {
  if (message && message.startsWith('{')) {
    try {
      const parsed = JSON.parse(message);
      if (parsed?.type === 'activity') {
        const count = parsed.photos?.length || 0;
        const caption = parsed.caption ? ': ' + parsed.caption : '';
        return `📷 ${count} foto kegiatan${caption}`;
      }
    } catch { /* not JSON */ }
  }
  return message;
};

// Subscribe to all group room Pusher channels for the current user
const subscribeGroupRooms = async () => {
  if (!pusher.value) return;

  try {
    const res = await api.get('/chat/rooms');
    const rooms = res.data?.data || [];

    rooms.forEach((room) => {
      // room.id format: "room-123"
      const numericId = String(room.id).replace('room-', '');
      const channelName = 'private-room-chat.' + numericId;

      // Avoid double-subscribing
      if (groupRoomChannels.value.find(c => c.name === channelName)) return;

      const ch = pusher.value.subscribe(channelName);

      ch.bind('message.sent', (data) => {
        // Suppress if user is already viewing this exact GroupChatRoom
        const isViewingThisRoom =
          route.name === 'GroupChatRoom' &&
          route.params.roomId === room.id;

        if (!isViewingThisRoom) {
          const senderLabel = data.sender_name || 'Guru';
          const preview = buildGroupMessagePreview(data.message);
          showNotification(`${room.name} — ${senderLabel}`, preview);
        }
      });

      groupRoomChannels.value.push(ch);
    });
  } catch (err) {
    console.error('Error subscribing to group room channels:', err);
  }
};

// Unsubscribe from all group room channels (called on logout)
const unsubscribeGroupRooms = () => {
  groupRoomChannels.value.forEach(ch => {
    try {
      ch.unbind_all();
      pusher.value?.unsubscribe(ch.name);
    } catch { /* ignore */ }
  });
  groupRoomChannels.value = [];
};

const fetchData = async () => {
  try {
    const profile = profileStore.profile;

    if (isParent.value) {
      await childrenStore.fetchChildrenList(profile.id);
    }

    initializePusher();

    // Subscribe to all group room channels for real-time notifications
    await subscribeGroupRooms();

    notificationStore.fetchNotificationCount();
  } catch (err) {
    console.error('Error in fetchData:', err);
  }
};

const handleNotificationClick = (notification) => {
  const redirect = notification.notification.extra?.redirectTo || notification.notification.data?.redirectTo;
  if (redirect === 'Notification') {
    router.push({ name: 'Notification' });
  }
};

const handleBackButton = () => {
  const currentPath = router.currentRoute.value.path;
  if (currentPath === '/home' && confirm('Apakah kamu ingin keluar dari aplikasi?')) {
    App.exitApp();
  } else {
    window.history.back();
  }
};

const retryFetch = async () => {
  errorLoader.value = false;
  isReady.value = false;
  await checkExcludedRoute();
  if (!isReady.value) errorLoader.value = true;
};

watch(() => route.path, (path) => {
  document.body.classList.toggle('scale-x-[-1]', path === '/scan-qr-code');
});

watch(() => route.fullPath, () => {
  checkExcludedRoute();
});

const initPush = async () => {
  // 1. Request permission
  const perm = await PushNotifications.requestPermissions();

  if (perm.receive !== 'granted') {
    console.log('Permission denied');
    return;
  }

  // 2. Register ke FCM
  await PushNotifications.register();

  // 3. Dapet token
  PushNotifications.addListener('registration', async token => {
    await api.post('/fcm/token', {
      token: token.value,
      platform: 'android',
    });
  });

  // 4. Error
  PushNotifications.addListener('registrationError', err => {
    console.error('Registration error:', err);
  });

  // 5. Notif masuk (foreground)
  PushNotifications.addListener('pushNotificationReceived', notif => {
    showNotification(notif.title, notif.body)
  });

  // 6. Notif di-tap
  PushNotifications.addListener('pushNotificationActionPerformed', action => {
    const data = action.notification.data

    if (data?.redirect) {
      router.push({ name: data.redirect, query: { type: data.type } })
    }
  });
};

onMounted(async () => {
  App.addListener('backButton', handleBackButton);
  LocalNotifications.addListener('localNotificationActionPerformed', handleNotificationClick);

  await router.isReady(); // pastiin route udah siap
  checkExcludedRoute();

  if (!isNative) return
  window.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchmove', onTouchMove, { passive: true })
  window.addEventListener('touchend', onTouchEnd)
  initPush()
});

onUnmounted(() => {
  window.removeEventListener('touchstart', onTouchStart, { passive: true })
  window.removeEventListener('touchmove', onTouchMove, { passive: true })
  window.removeEventListener('touchend', onTouchEnd)
  window.removeEventListener('force-refetch', checkExcludedRoute);
});

const notification = ref(false);
const notificationTitle = ref('');
const notificationDescription = ref('');

const showNotification = (title, body) => {
  notification.value = true
  notificationTitle.value = title
  notificationDescription.value = body
  
  setTimeout(() => {
    notification.value = false
  }, 3000)
}
</script>

<style scoped>
@keyframes toast-in {
  0% {
    transform: translate(-50%, -7rem);
  }
  70% {
    transform: translate(-50%, 1.5rem); /* translate-y-6 */
  }
  100% {
    transform: translate(-50%, 1rem); /* translate-y-4 */
  }
}

@keyframes toast-out {
  0% {
    transform: translate(-50%, 1rem);
    opacity: 1;
  }
  70% {
    transform: translate(-50%, 1.5rem); /* translate-y-6 */
  }
  100% {
    transform: translate(-50%, -7rem);
    opacity: 0;
  }
}

@keyframes toast-progress {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.animate-toast-in {
  animation: toast-in 0.4s cubic-bezier(.34,1.56,.64,1) forwards;
}

.animate-toast-out {
  animation: toast-out 0.25s ease-in forwards;
}

.animate-toast-progress {
  animation: toast-progress 3s linear forwards;
}
</style>